import { useState } from 'react';
import { cn } from '../lib/utils';
import { Icon } from './ui/icons';

interface Project {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'draft' | 'archived';
  repositories_count: number;
  compliance_score: number;
  frameworks: string[];
  owner: string;
  created_at: string;
  updated_at: string;
}

interface ProjectsProps {
  navigate: (route: string) => void;
}

const mockProjects: Project[] = [
  {
    id: '1',
    name: 'CyLift Dashboard',
    description: 'Unified security and compliance dashboard for enterprise monitoring',
    status: 'active',
    repositories_count: 4,
    compliance_score: 92,
    frameworks: ['SOC2', 'ISO27001'],
    owner: 'Security Team',
    created_at: '2024-01-15',
    updated_at: '2024-05-22'
  },
  {
    id: '2', 
    name: 'API Gateway Services',
    description: 'Microservices architecture for API management and authentication',
    status: 'active',
    repositories_count: 8,
    compliance_score: 87,
    frameworks: ['GDPR', 'SOC2'],
    owner: 'Platform Team',
    created_at: '2024-02-20',
    updated_at: '2024-05-20'
  },
  {
    id: '3',
    name: 'Data Processing Pipeline',
    description: 'ETL pipeline for security event processing and analysis',
    status: 'draft',
    repositories_count: 3,
    compliance_score: 78,
    frameworks: ['GDPR'],
    owner: 'Data Team',
    created_at: '2024-03-10',
    updated_at: '2024-05-18'
  },
  {
    id: '4',
    name: 'Mobile Application',
    description: 'Customer-facing mobile app with security monitoring',
    status: 'active',
    repositories_count: 2,
    compliance_score: 95,
    frameworks: ['SOC2', 'PCI-DSS'],
    owner: 'Mobile Team',
    created_at: '2024-01-05',
    updated_at: '2024-05-21'
  }
];

function ProjectCard({ project, onClick }: { project: Project; onClick: () => void }) {
  const statusColor = {
    active: 'text-success-600 bg-success-50',
    draft: 'text-warning-600 bg-warning-50', 
    archived: 'text-gray-600 bg-gray-50'
  };

  const statusLabel = {
    active: 'Active',
    draft: 'Draft',
    archived: 'Archived'
  };

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-5 rounded-[10px] border border-gray-200 bg-white hover:border-primary-300 hover:shadow-[0_2px_3px_0_rgba(0,0,0,0.06)] transition"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-display font-bold text-ink text-[16px] mb-1">{project.name}</h3>
          <p className="font-display text-[14px] text-ink/70 mb-2 line-clamp-2">{project.description}</p>
        </div>
        <span className={cn('px-2 py-1 rounded-full text-[11px] font-bold', statusColor[project.status])}>
          {statusLabel[project.status]}
        </span>
      </div>

      <div className="flex items-center justify-between text-[13px] text-ink/60 mb-3">
        <span className="flex items-center gap-1">
          <Icon name="book" size={14} />
          {project.repositories_count} repositories
        </span>
        <span className="flex items-center gap-1">
          <Icon name="shield" size={14} />
          {project.compliance_score}% compliance
        </span>
        <span>{project.owner}</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {project.frameworks.map(framework => (
            <span 
              key={framework}
              className="px-2 py-1 text-[10px] font-bold bg-primary-50 text-primary-600 rounded"
            >
              {framework}
            </span>
          ))}
        </div>
        <Icon name="chevron-right" size={16} className="text-ink/40" />
      </div>
    </button>
  );
}

export default function Projects({ navigate }: ProjectsProps) {
  const [projects] = useState<Project[]>(mockProjects);
  const [filter, setFilter] = useState<'all' | 'active' | 'draft' | 'archived'>('all');

  const filteredProjects = projects.filter(project => 
    filter === 'all' || project.status === filter
  );

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <h1 className="font-display font-bold text-ink" style={{ fontSize: 36, lineHeight: 1.05 }}>
            Projects
          </h1>
          <p className="font-display text-[18px] font-medium text-ink/70 mt-2">
            Manage compliance tracking across your projects and repositories.
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('projects/new')}
            className="h-10 px-4 rounded-[10px] bg-primary-500 text-white font-display font-bold text-[15px] inline-flex items-center gap-2 hover:bg-primary-600 transition"
          >
            <Icon name="plus" size={16} />
            New Project
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {[
          { id: 'all', label: 'All Projects', count: projects.length },
          { id: 'active', label: 'Active', count: projects.filter(p => p.status === 'active').length },
          { id: 'draft', label: 'Draft', count: projects.filter(p => p.status === 'draft').length },
          { id: 'archived', label: 'Archived', count: projects.filter(p => p.status === 'archived').length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id as any)}
            className={cn(
              'px-4 py-2 font-display text-[14px] font-medium border-b-2 transition',
              filter === tab.id
                ? 'text-primary-600 border-primary-500'
                : 'text-ink/60 border-transparent hover:text-ink hover:border-gray-300'
            )}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {filteredProjects.map(project => (
          <ProjectCard
            key={project.id}
            project={project}
            onClick={() => navigate(`projects/${project.id}`)}
          />
        ))}
      </div>

      {/* Empty State */}
      {filteredProjects.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Icon name="book" size={48} className="text-ink/20 mb-4" />
          <h3 className="font-display font-semibold text-ink text-[18px] mb-2">
            No {filter !== 'all' && filter} projects found
          </h3>
          <p className="font-display text-[14px] text-ink/60 mb-6 max-w-md">
            {filter === 'all' 
              ? "Get started by creating your first project to track compliance across repositories."
              : `No projects with status "${filter}" at the moment.`
            }
          </p>
          {filter === 'all' && (
            <button
              onClick={() => navigate('projects/new')}
              className="px-4 py-2 bg-primary-500 text-white font-display font-semibold rounded-lg hover:bg-primary-600 transition"
            >
              Create First Project
            </button>
          )}
        </div>
      )}
    </div>
  );
}